import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { promptRequest } from '@/hooks/prompts/lib'
import { promptQueryKeys, promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { SavedPrompt } from '@/types/library/SavedPrompt'

interface UseIncrementUsageArgs {
  orgId: UUID | ''
}

interface IncrementUsageRequest {
  promptId: UUID
  sessionId?: UUID | null
}

const incrementUsage = async (
  request: IncrementUsageRequest,
  accessToken: string | undefined | null,
): Promise<SavedPrompt> =>
  promptRequest<SavedPrompt>(
    'POST',
    `${API_URL}/api/v1/prompts/${request.promptId}/increment-usage`,
    accessToken,
    { session_id: request.sessionId ?? null },
  )

export const useIncrementUsage = ({ orgId }: UseIncrementUsageArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    SavedPrompt,
    Error,
    IncrementUsageRequest,
    PaginatedResponse<SavedPrompt> | undefined
  >({
    mutationFn: (request) => incrementUsage(request, accessToken),
    onMutate: async ({ promptId }) => {
      const key = promptsQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key, exact: true })

      // snapshot the previous value
      const previousData =
        queryClient.getQueryData<PaginatedResponse<SavedPrompt>>(key)

      // optimistically update usage count
      if (previousData) {
        const updatedData = previousData.data.map((p) =>
          p.id === promptId ? { ...p, usage_count: p.usage_count + 1 } : p,
        )
        queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(key, {
          count: previousData.count,
          data: updatedData,
          has_more: previousData.has_more,
        })
      }

      // return rollback value
      return previousData
    },
    onError: (err, _req, context) => {
      const key = promptsQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, { promptId }) => {
      const listKey = promptsQueryKeys.organization({ orgId })
      const singleKey = promptQueryKeys.prompt({ promptId })
      queryClient.setQueryData<SavedPrompt>(singleKey, data)
      // The response is authoritative for this prompt; patch it into the
      // cached list instead of refetching the whole collection.
      queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(
        listKey,
        (previous) =>
          previous && {
            ...previous,
            data: previous.data.map((p) => (p.id === promptId ? data : p)),
          },
      )
      void queryClient.invalidateQueries({
        queryKey: promptQueryKeys.activity({ promptId }),
      })
    },
  })
}
